import { useEffect, useId, useMemo } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { $countriesStore, $recipientCreateStore, $recipientMandatoryFieldsStore } from '@lib/store';
import { PaymentUtils } from '@shared/utils';
import { TransferRecipientTypeEnum, VopStatus } from '@shared/api';
import {
  AccountFormBlock,
  AddressFormBlock,
  ConfirmModal,
  PersonalFormBlock,
  RecipientVerificationModal,
  VerifiedRecipientModal,
  type AccountFormData,
  type AddressFormData,
  type PersonalFormData,
} from '@widgets';
import { Button, Spinner } from '@shared/widgets';
import ConfirmIcon from '@assets/icons/confirm.svg';

type IndividualRecipientFormData = PersonalFormData & AddressFormData & AccountFormData;
const DEFAULT_VALUES: IndividualRecipientFormData = {
  firstName: '',
  lastName: '',
  idNumber: '',
  email: '',
  address: '',
  postCode: '',
  city: '',
  province: '',
  companyCountry: '',
  iban: '',
  currency: '',
  bankName: '',
  bankCountry: '',
  accountNumber: '',
  bic: '',
  accountType: undefined,
  bankProvince: '',
  bankAddress: '',
  bankCity: '',
  branchCode: '',
};

export const IndividualRecipient = () => {
  const countriesStore = useUnit($countriesStore.store);
  const recipientMandatoryFieldsStore = useUnit($recipientMandatoryFieldsStore.store);
  const recipientStore = useUnit($recipientCreateStore.store);

  const formId = useId();
  const formControl = useForm<IndividualRecipientFormData>({ defaultValues: DEFAULT_VALUES });
  const { formState, handleSubmit, getValues, watch } = formControl;
  const [bankCountry, companyCountry, currency] = watch(['bankCountry', 'companyCountry', 'currency']);

  const { t } = useTranslation('recipientEditor');

  useEffect(() => {
    return () => {
      $recipientMandatoryFieldsStore.events.clear();
      $recipientCreateStore.events.clear();
    };
  }, []);

  const countryOptions = useMemo(
    () => countriesStore.data.map((country) => ({ label: country.name, value: country.twoLetterCode })),
    [countriesStore],
  );
  useEffect(() => {
    const residenceCountryCode = countryOptions.find((option) => option.value === companyCountry)?.value;
    const bankCountryCode = countryOptions.find((option) => option.value === bankCountry)?.value;
    if (bankCountryCode && residenceCountryCode && currency) {
      $recipientMandatoryFieldsStore.events.getMandatoryFields({
        residenceCountryCode,
        bankCountryCode,
        currency,
        type: TransferRecipientTypeEnum.Individual,
      });
    }
  }, [bankCountry, companyCountry, countryOptions, currency]);

  const submitHandler = async (data: IndividualRecipientFormData) =>
    await PaymentUtils.createRecipient({ ...data, type: TransferRecipientTypeEnum.Individual });
  const submitWithForceUpdate = async () =>
    await PaymentUtils.createRecipient({ ...getValues(), type: TransferRecipientTypeEnum.Individual, vopForceUpdate: true });

  return (
    <div className='flex w-full flex-col'>
      {(formState.isSubmitting || recipientStore.loading) && <Spinner position />}
      <FormProvider {...formControl}>
        <form id={formId} className='flex flex-col gap-6 bg-white dark:bg-soft-black' noValidate onSubmit={handleSubmit(submitHandler)}>
          <PersonalFormBlock />
          <AddressFormBlock />
          <AccountFormBlock />
        </form>
        <Button
          className='mt-5 max-w-72'
          color='primary'
          type='submit'
          form={formId}
          disabled={formState.isSubmitting || recipientStore.loading || recipientMandatoryFieldsStore.loading}
        >
          {t('buttons.addNewRecipient')}
        </Button>
      </FormProvider>
      <ConfirmModal
        isOpen={!!recipientStore.data?.vopStatus && recipientStore.data.vopStatus !== VopStatus.Verified}
        icon={<ConfirmIcon />}
        title={t('modals.confirm.title')}
      />
      <VerifiedRecipientModal isOpen={recipientStore.data?.vopStatus === VopStatus.Verified} description='A new recipient was added' />
      <RecipientVerificationModal
        isOpen={!!recipientStore.error?.errorCode}
        onClose={() => $recipientCreateStore.events.clear()}
        type='createRecipient'
        error={recipientStore.error}
        onSubmit={submitWithForceUpdate}
      />
    </div>
  );
};
